import React from 'react';
import { FaGlobe, FaHeart } from 'react-icons/fa';
import type { QuestionResult } from '../types';

interface StatsBarProps {
  score: number;
  vitality: number;
  lastDelta: number | null;
  questionResults: QuestionResult[];
  currentQuestionIndex: number;
  year?: number;
  sessionLabel?: string;
  progressLabel?: string;
  showQuestionBadges?: boolean;
  showVitality?: boolean;
}

const BADGE_BACKGROUNDS: Record<QuestionResult, string> = {
  correct: 'radial-gradient(circle at 35% 35%, #D9FFF0 0%, #22C55E 45%, #14532D 100%)',
  wrong: 'radial-gradient(circle at 35% 35%, #FFD9D6 0%, #FF3B30 45%, #7F1D1D 100%)',
  pending: 'radial-gradient(circle at 35% 35%, #EFE9EA 0%, #C9BCC0 38%, #5C4C52 100%)',
};

const StatsBar: React.FC<StatsBarProps> = ({
  score,
  vitality,
  lastDelta,
  questionResults,
  currentQuestionIndex,
  year = 1,
  sessionLabel,
  progressLabel,
  showQuestionBadges = true,
  showVitality = true,
}) => {
  const badges = Array.from({ length: 5 }, (_, i) => {
    const result = questionResults[i];
    const isActive = i === currentQuestionIndex;
    const isCompleted = result !== undefined;

    return {
      result: result || 'pending',
      isActive,
      isCompleted,
    };
  });

  const pillStyle: React.CSSProperties = {
    border: '0.75px solid #334155',
    background: '#2E3A66',
    borderRadius: 'clamp(8px, 1.5vw, 16px)',
    fontFamily: 'Inter, system-ui, sans-serif',
    color: '#F8FAFC',
    letterSpacing: '0.06em',
    padding: 'clamp(4px, 0.6vw, 12px) clamp(6px, 1vw, 24px)',
  };

  return (
    <div
      className="relative mx-auto flex flex-col sm:flex-row items-stretch sm:items-center justify-between overflow-hidden px-2 py-2 sm:px-4 sm:py-3 gap-2 sm:gap-0"
      style={{
        width: '80%',
        maxWidth: '891px',
        background: 'linear-gradient(180deg, rgba(39, 49, 93, 0.96) 0%, rgba(41, 50, 93, 0.8) 100%)',
        borderRadius: '16px',
      }}
    >
      <div
        className="absolute left-0 right-0 top-1 sm:top-2 mx-auto"
        style={{
          width: '100%',
          height: '1px',
          background: 'linear-gradient(90deg, rgba(14, 133, 120, 0.00) 0%, #14B8A6 49.52%, rgba(9, 82, 74, 0.00) 100%)',
          boxShadow: '0 4px 4px 0 rgba(20, 184, 166, 0.40)',
        }}
      />
      <div
        className="absolute left-0 right-0 bottom-1 sm:bottom-2 mx-auto"
        style={{
          width: '100%',
          height: '1px',
          background: 'linear-gradient(90deg, rgba(14, 133, 120, 0.00) 0%, #14B8A6 49.52%, rgba(9, 82, 74, 0.00) 100%)',
          boxShadow: '0 4px 4px 0 rgba(20, 184, 166, 0.40)',
        }}
      />

      {/* Row 1 on narrow screens: Year/Date + Score. Unwraps into the main row on sm+. */}
      <div className="relative z-10 flex items-center justify-between sm:contents">
        <div
          className="inline-flex items-center justify-center shrink-0 stats-pill-text sm:order-1"
          style={pillStyle}
        >
          {sessionLabel ? (
            <span>{sessionLabel}</span>
          ) : (
            <>
              <span>Year {year}: </span>
              <span style={{ color: '#FFE896', marginLeft: '6px' }}>{2020 + year - 1}</span>
            </>
          )}
        </div>

        <div
          className="inline-flex items-center shrink-0 stats-pill-text sm:order-3"
          style={{
            ...pillStyle,
            gap: 'clamp(4px, 0.6vw, 8px)',
            marginRight: showVitality ? undefined : '0px',
          }}
        >
          <FaGlobe style={{ width: 'clamp(10px, 1.2vw, 20px)', height: 'clamp(10px, 1.2vw, 20px)' }} className="text-white" />
          <span>Score: {Math.floor(score)}</span>
        </div>
      </div>

      {/* Row 2 on narrow screens: progress badges / label + vitality. Unwraps into the main row on sm+. */}
      <div className="relative z-10 flex items-center justify-between sm:contents">
        {showQuestionBadges ? (
          <div className="flex items-center sm:order-2" style={{ gap: 'clamp(4px, 0.8vw, 12px)' }}>
            {badges.map((badge, i) => (
              <div
                key={i}
                className="relative flex items-center justify-center rounded-full transition-all duration-300"
                style={{
                  width: 'clamp(18px, 2.2vw, 36px)',
                  height: 'clamp(18px, 2.2vw, 36px)',
                  border: '1px solid #5B4B45',
                  background: badge.isActive
                    ? 'radial-gradient(circle at 35% 35%, #FFE896 0%, #FACC15 42%, #8A6A12 100%)'
                    : BADGE_BACKGROUNDS[badge.result],
                  boxShadow: badge.isActive
                    ? '0 0 14px rgba(250, 204, 21, 0.45)'
                    : '0 2px 4px rgba(0, 0, 0, 0.28)',
                }}
              >
                <div
                  className="rounded-full"
                  style={{
                    width: 'clamp(10px, 1.2vw, 20px)',
                    height: 'clamp(10px, 1.2vw, 20px)',
                    border: '1px solid rgba(255,255,255,0.4)',
                    background: 'rgba(255,255,255,0.45)',
                  }}
                />
              </div>
            ))}
          </div>
        ) : (
          <div
            className="inline-flex items-center justify-center stats-pill-text sm:order-2"
            style={pillStyle}
          >
            <span>{progressLabel ?? 'Custom Game'}</span>
          </div>
        )}

        {showVitality && (
          <div
            className="inline-flex items-center shrink-0 stats-pill-text sm:order-4"
            style={{ ...pillStyle, gap: 'clamp(4px, 0.6vw, 8px)' }}
          >
            <FaHeart style={{ width: 'clamp(10px, 1.2vw, 20px)', height: 'clamp(10px, 1.2vw, 20px)', flexShrink: 0 }} className="text-red-400" />
            <div style={{ position: 'relative', width: 'clamp(70px, 9vw, 140px)', height: 'clamp(12px, 1.6vw, 20px)', flexShrink: 0 }}>
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'rgba(255,255,255,0.12)',
                  borderRadius: '999px',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${Math.min(100, Math.max(0, vitality))}%`,
                    height: '100%',
                    borderRadius: '999px',
                    background: vitality > 50 ? '#22C55E' : vitality > 25 ? '#ff9900' : '#EF4444',
                    transition: 'width 0.4s ease, background 0.4s ease',
                  }}
                />
              </div>
              <span
                className="stats-vitality-pct-text"
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 'clamp(7px, 0.85vw, 11px)',
                  fontWeight: 600,
                  color: '#fff',
                  textShadow: '0 1px 2px rgba(0,0,0,0.6)',
                  letterSpacing: '0.04em',
                }}
              >
                {Math.floor(vitality)}%{lastDelta !== null && Math.round(lastDelta) !== 0 && vitality < 100 ? ` (${lastDelta > 0 ? '+' : ''}${Math.round(lastDelta)})` : ''}
              </span>
            </div>
          </div>
        )}
      </div>

      <style>{`
        .stats-pill-text {
          font-size: clamp(9px, 1.1vw, 14px);
        }
        @media (max-width: 639px) {
          .stats-pill-text {
            font-size: 13px !important;
          }
          .stats-vitality-pct-text {
            font-size: 10px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default StatsBar;